import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useConfig } from '../context/ConfigContext';

function PaymentTracking() {
  const navigate = useNavigate();
  const { isDemoMode } = useConfig();
  const demoDisabled = isDemoMode();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'down_payment', 'monthly_payment'
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const loadPayments = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/payments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPayments(response.data);
    } catch (err) {
      console.error('Failed to load payments:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

const deletePayment = async (payment) => {
    // First confirmation
    if (!window.confirm(
      `⚠️ DELETE PAYMENT?\n\n` +
      `Amount: $${parseFloat(payment.amount).toFixed(2)}\n` +
      `Customer: ${payment.customer_name}\n` +
      `Date: ${new Date(payment.payment_date).toLocaleDateString()}\n` +
      `Type: ${payment.payment_type}\n\n` +
      `This will add the loan portion back to the balance.\n\n` +
      `Continue?`
    )) return;

    // The reason, recorded permanently in the deletion audit.
    //
    // Asking here is a convenience, not the guard: the server rejects this
    // request outright without a reason, because an audit row that records
    // money disappearing and nothing about why is most of the value gone.
    // Cancelling the prompt cancels the deletion; an empty or blank reason is
    // refused rather than quietly sent.
    const reason = prompt(
      'Why is this payment being deleted?\n\n' +
      'This is recorded permanently against the loan and appears on the escrow report.'
    );
    if (reason === null) return;
    if (!reason.trim()) {
      alert('A reason is required. Deletion cancelled.');
      return;
    }

    // Second confirmation
    const confirmation = prompt('Type DELETE to confirm:');
    if (confirmation?.toUpperCase() !== 'DELETE') {
      alert('Deletion cancelled');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/admin/payments/${payment.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          // axios.delete takes a CONFIG object, not a body, so the request body
          // goes under `data`. Passing it as a second positional argument the
          // way axios.post does would send it as the config and drop it.
          data: { reason: reason.trim() }
        }
      );

      alert(`Payment deleted successfully!\n\n$${response.data.amountAddedBack.toFixed(2)} added back to loan balance.\n\nNew Balance: $${response.data.newBalance.toFixed(2)}`);
      loadPayments(); // Reload the list
    } catch (err) {
      console.error('Failed to delete payment:', err);
      alert('Failed to delete payment: ' + (err.response?.data?.error || err.message));
    }
  };

  // Filter payments
  const filteredPayments = payments.filter(payment => {
    // Filter by payment type
    if (filter !== 'all' && payment.payment_type !== filter) return false;

    // Filter by payment method
    if (paymentMethodFilter !== 'all' && payment.payment_method !== paymentMethodFilter) return false;

    // Filter by customer
    if (customerFilter !== 'all' && payment.customer_name !== customerFilter) return false;

    // Filter by search term (customer name, email, or property)
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesCustomer = payment.customer_name?.toLowerCase().includes(search);
      const matchesEmail = payment.customer_email?.toLowerCase().includes(search);
      const matchesProperty = payment.property_title?.toLowerCase().includes(search);
      if (!matchesCustomer && !matchesEmail && !matchesProperty) return false;
    }

    // Filter by date range
    if (startDate && new Date(payment.payment_date) < new Date(startDate)) return false;
    if (endDate && new Date(payment.payment_date) > new Date(endDate)) return false;

    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPayments = filteredPayments.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, paymentMethodFilter, customerFilter, searchTerm, startDate, endDate]);

  // Calculate statistics
  const totalCollected = filteredPayments.reduce((sum, p) => sum + parseFloat(p.amount), 0);
  const thisMonth = new Date();
  const thisMonthPayments = filteredPayments.filter(p => {
    const paymentDate = new Date(p.payment_date);
    return paymentDate.getMonth() === thisMonth.getMonth() && 
           paymentDate.getFullYear() === thisMonth.getFullYear();
  });
  const thisMonthTotal = thisMonthPayments.reduce((sum, p) => sum + parseFloat(p.amount), 0);
  
  const today = new Date();
  const todayPayments = filteredPayments.filter(p => {
    const paymentDate = new Date(p.payment_date);
    return paymentDate.toDateString() === today.toDateString();
  });

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading payments...</div>;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 className="admin-page-title" style={{ margin: '0 0 5px 0' }}>💳 Payment Tracking</h1>
        <p style={{ margin: '0 0 20px 0', color: 'var(--color-text-light)', textAlign: 'center' }}>
          {filteredPayments.length} payments shown
        </p>
        <button onClick={() => navigate('/admin/customers-loans-hub')} className="admin-hub-back-btn">
          <span className="admin-hub-back-arrow">←</span>
          Back to Customers & Loans
        </button>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
      }}>
        <div className="card" style={{ textAlign: 'center', padding: '30px' }}>
          <h3 style={{ fontSize: '36px', margin: '0 0 10px 0', color: 'var(--color-brand-text)' }}>
            ${totalCollected.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </h3>
          <p style={{ margin: 0, color: 'var(--color-text-light)' }}>Total Collected (Filtered)</p>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '30px' }}>
          <h3 style={{ fontSize: '36px', margin: '0 0 10px 0', color: 'var(--sandy-gold)' }}>
            ${thisMonthTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </h3>
          <p style={{ margin: 0, color: 'var(--color-text-light)' }}>This Month ({thisMonthPayments.length} payments)</p>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '30px' }}>
          <h3 style={{ fontSize: '36px', margin: '0 0 10px 0', color: '#6366f1' }}>
            {todayPayments.length}
          </h3>
          <p style={{ margin: 0, color: 'var(--color-text-light)' }}>Payments Today</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ padding: '20px', marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          {/* Payment Type Filter */}
          <div>
            <label className="form-label">
              Payment Type
            </label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="form-select"
            >
              <option value="all">All Payments</option>
              <option value="down_payment">Down Payments</option>
              <option value="monthly_payment">Monthly Payments</option>
              <option value="processing_fee">Processing Fees</option>
            </select>
          </div>

          {/* Payment Method Filter */}
          <div>
            <label className="form-label">
              Payment Method
            </label>
            <select
              value={paymentMethodFilter}
              onChange={(e) => setPaymentMethodFilter(e.target.value)}
              className="form-select"
            >
              <option value="all">All Methods</option>
              <option value="square">Square</option>
              <option value="cash">Cash</option>
              <option value="check">Check</option>
              <option value="venmo">Venmo</option>
              <option value="zelle">Zelle</option>
              <option value="wire_transfer">Wire Transfer</option>
              <option value="money_order">Money Order</option>
              <option value="custom_loan">Custom Loan</option>
              <option value="imported">Imported</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Customer Filter */}
          <div>
            <label className="form-label">
              Customer
            </label>
            <select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              className="form-select"
            >
              <option value="all">All Customers</option>
              {[...new Set(payments.map(p => p.customer_name))].filter(Boolean).sort().map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="form-label">
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Customer or property..."
              className="form-input"
            />
          </div>

          {/* Start Date */}
          <div>
            <label className="form-label">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="form-input"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="form-label">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="form-input"
            />
          </div>
        </div>

        {/* Clear Filters */}
        {(filter !== 'all' || paymentMethodFilter !== 'all' || customerFilter !== 'all' || searchTerm || startDate || endDate) && (
          <button
            onClick={() => {
              setFilter('all');
              setPaymentMethodFilter('all');
              setCustomerFilter('all');
              setSearchTerm('');
              setStartDate('');
              setEndDate('');
            }}
            className="btn"
            style={{ marginTop: '15px' }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Payments Table - Desktop */}
      <div className="card desktop-only" style={{ padding: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--light-green)', borderBottom: '2px solid var(--forest-green)' }}>
              <th style={{ padding: '15px', textAlign: 'left' }}>Date</th>
              <th style={{ padding: '15px', textAlign: 'left' }}>Customer</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Property</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>Type</th>
              <th style={{ padding: '12px', textAlign: 'right' }}>Amount</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Breakdown</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedPayments.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-light)' }}>
                  No payments found
                </td>
              </tr>
            ) : (
              paginatedPayments.map(payment => (
                <tr key={payment.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '15px' }}>
                    {new Date(payment.payment_date).toLocaleDateString()}
                    <br />
                    <small style={{ color: 'var(--color-text-muted)' }}>
                      {new Date(payment.payment_date).toLocaleTimeString()}
                    </small>
                  </td>
                  <td style={{ padding: '15px' }}>
                    <strong>{payment.customer_name}</strong>
                    <br />
                    <small style={{ color: 'var(--color-text-light)' }}>{payment.customer_email}</small>
                    {payment.customer_phone && (
                      <>
                        <br />
                        <small style={{ color: 'var(--color-text-light)' }}>{payment.customer_phone}</small>
                      </>
                    )}
                  </td>
                  <td style={{ padding: '15px' }}>
                    {payment.property_title || '—'}
                  </td>
                  <td style={{ padding: '15px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      backgroundColor: payment.payment_type === 'down_payment' ? '#e0f2fe' : payment.payment_type === 'processing_fee' ? '#fef3c7' : '#f0fdf4',
                      color: payment.payment_type === 'down_payment' ? '#0369a1' : payment.payment_type === 'processing_fee' ? '#92400e' : '#15803d'
                    }}>
                      {payment.payment_type === 'down_payment' ? 'DOWN' : payment.payment_type === 'processing_fee' ? 'DOC FEE' : 'MONTHLY'}
                    </span>
                  </td>
                  <td style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold', fontSize: '16px' }}>
                    ${parseFloat(payment.amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </td>
                  <td style={{ padding: '15px', fontSize: '13px', lineHeight: '1.5' }}>
                    {payment.loan_payment_amount > 0 && (
                      <>
                        <div>💰 Loan: ${parseFloat(payment.loan_payment_amount).toFixed(2)}</div>
                        {payment.principal_amount > 0 && (
                          <div style={{ paddingLeft: '20px', color: 'var(--color-success-text)' }}>
                            ↳ Principal: ${parseFloat(payment.principal_amount).toFixed(2)}
                          </div>
                        )}
                        {payment.interest_amount > 0 && (
                          <div style={{ paddingLeft: '20px', color: '#f59e0b' }}>
                            ↳ Interest: ${parseFloat(payment.interest_amount).toFixed(2)}
                          </div>
                        )}
                      </>
                    )}
                    {payment.tax_amount > 0 && (
                      <div>🏛️ Tax: ${parseFloat(payment.tax_amount).toFixed(2)}</div>
                    )}
                    {payment.hoa_amount > 0 && (
                      <div>🏘️ HOA: ${parseFloat(payment.hoa_amount).toFixed(2)}</div>
                    )}
                    {payment.late_fee_amount > 0 && (
                      <div>⏰ Late Fee: ${parseFloat(payment.late_fee_amount).toFixed(2)}</div>
                    )}
                    {payment.notice_fee_amount > 0 && (
                      <div>📄 Notice Fee: ${parseFloat(payment.notice_fee_amount).toFixed(2)}</div>
                    )}
                    {payment.postal_fee_amount > 0 && (
                      <div>📮 Postal: ${parseFloat(payment.postal_fee_amount).toFixed(2)}</div>
                    )}
                    {payment.shortfall_amount > 0 && (
                      <div style={{ color: 'var(--color-error-text)' }}>
                        ⚠️ Shortfall carried: ${parseFloat(payment.shortfall_amount).toFixed(2)}
                      </div>
                    )}
                    {payment.arrears_cleared > 0 && (
                      <div style={{ color: 'var(--color-success-text)' }}>
                        ✓ Arrears paid down: ${parseFloat(payment.arrears_cleared).toFixed(2)}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '15px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      backgroundColor: '#10b981',
                      color: 'white'
                    }}>
                      {payment.status.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: '15px', textAlign: 'center' }}>
                    <button
                      onClick={() => !demoDisabled && deletePayment(payment)}
                      disabled={demoDisabled}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: demoDisabled ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        fontWeight: '600',
                        opacity: demoDisabled ? 0.5 : 1
                      }}
                      onMouseOver={(e) => !demoDisabled && (e.target.style.backgroundColor = '#c82333')}
                      onMouseOut={(e) => !demoDisabled && (e.target.style.backgroundColor = '#dc3545')}
                      title={demoDisabled ? 'Disabled in demo mode' : ''}
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Payments Cards - Mobile */}
      <div className="mobile-only">
        {paginatedPayments.length === 0 ? (
          <div className="card" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-light)' }}>
            No payments found
          </div>
        ) : (
          paginatedPayments.map(payment => (
            <div key={payment.id} className="card" style={{ marginBottom: '15px', padding: '15px' }}>
              {/* Date & Type Badge */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                    {new Date(payment.payment_date).toLocaleDateString()}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                    {new Date(payment.payment_date).toLocaleTimeString()}
                  </div>
                </div>
                <span style={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  backgroundColor: payment.payment_type === 'down_payment' ? '#e0f2fe' : payment.payment_type === 'processing_fee' ? '#fef3c7' : '#f0fdf4',
                  color: payment.payment_type === 'down_payment' ? '#0369a1' : payment.payment_type === 'processing_fee' ? '#92400e' : '#15803d'
                }}>
                  {payment.payment_type === 'down_payment' ? 'DOWN' : payment.payment_type === 'processing_fee' ? 'DOC FEE' : 'MONTHLY'}
                </span>
              </div>

              {/* Customer Info */}
              <div style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid var(--color-border)' }}>
                <div style={{ fontWeight: 'bold', fontSize: '15px', color: 'var(--color-brand-text)' }}>
                  {payment.customer_name}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--color-text-light)' }}>{payment.customer_email}</div>
                {payment.customer_phone && (
                  <div style={{ fontSize: '13px', color: 'var(--color-text-light)' }}>{payment.customer_phone}</div>
                )}
              </div>

              {/* Property */}
              <div style={{ marginBottom: '10px', fontSize: '14px' }}>
                <strong>Property:</strong> {payment.property_title || '—'}
              </div>

              {/* Amount */}
              <div style={{ marginBottom: '10px', fontSize: '20px', fontWeight: 'bold', color: 'var(--color-brand-text)' }}>
                ${parseFloat(payment.amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </div>

              {/* Breakdown */}
              <div style={{ fontSize: '13px', lineHeight: '1.6', backgroundColor: 'var(--color-surface-sunken)', padding: '10px', borderRadius: '5px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Breakdown:</div>
                {payment.loan_payment_amount > 0 && (
                  <>
                    <div>💰 Loan: ${parseFloat(payment.loan_payment_amount).toFixed(2)}</div>
                    {payment.principal_amount > 0 && (
                      <div style={{ paddingLeft: '20px', color: 'var(--color-success-text)' }}>
                        ↳ Principal: ${parseFloat(payment.principal_amount).toFixed(2)}
                      </div>
                    )}
                    {payment.interest_amount > 0 && (
                      <div style={{ paddingLeft: '20px', color: '#f59e0b' }}>
                        ↳ Interest: ${parseFloat(payment.interest_amount).toFixed(2)}
                      </div>
                    )}
                  </>
                )}
                {payment.tax_amount > 0 && (
                  <div>🏛️ Tax: ${parseFloat(payment.tax_amount).toFixed(2)}</div>
                )}
                {payment.hoa_amount > 0 && (
                  <div>🏘️ HOA: ${parseFloat(payment.hoa_amount).toFixed(2)}</div>
                )}
                {payment.late_fee_amount > 0 && (
                  <div>⏰ Late Fee: ${parseFloat(payment.late_fee_amount).toFixed(2)}</div>
                )}
                {payment.notice_fee_amount > 0 && (
                  <div>📄 Notice Fee: ${parseFloat(payment.notice_fee_amount).toFixed(2)}</div>
                )}
                {payment.postal_fee_amount > 0 && (
                  <div>📮 Postal: ${parseFloat(payment.postal_fee_amount).toFixed(2)}</div>
                )}
                {payment.shortfall_amount > 0 && (
                  <div style={{ color: 'var(--color-error-text)' }}>
                    ⚠️ Shortfall carried: ${parseFloat(payment.shortfall_amount).toFixed(2)}
                  </div>
                )}
                {payment.arrears_cleared > 0 && (
                  <div style={{ color: 'var(--color-success-text)' }}>
                    ✓ Arrears paid down: ${parseFloat(payment.arrears_cleared).toFixed(2)}
                  </div>
                )}
              </div>

              {/* Status */}
              <div style={{ marginTop: '10px', textAlign: 'center' }}>
                <span style={{
                  padding: '6px 16px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  backgroundColor: '#10b981',
                  color: 'white'
                }}>
                  {payment.status.toUpperCase()}
                </span>
              </div>

              {/* Delete Button */}
              <button
                onClick={() => !demoDisabled && deletePayment(payment)}
                disabled={demoDisabled}
                style={{
                  width: '100%',
                  marginTop: '10px',
                  padding: '10px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: demoDisabled ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  opacity: demoDisabled ? 0.5 : 1
                }}
                title={demoDisabled ? 'Disabled in demo mode' : ''}
              >
                🗑️ Delete Payment
              </button>
            </div>
          ))
        )}
      </div>

      {/* Pagination Controls */}
      {filteredPayments.length > 0 && (
        <div className="card" style={{ padding: '20px', marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '15px', alignItems: 'center' }}>
          {/* Items per page selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <span style={{ fontSize: '14px', color: 'var(--color-text-light)' }}>Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '6px 10px',
                borderRadius: '4px',
                border: '1px solid var(--color-border)',
                /* Sized to its content, not full width, so it stays inline in
                   the pagination bar - .form-select would stretch it to 100%. */
                fontSize: '16px',
                minHeight: '44px',
                boxSizing: 'border-box'
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={filteredPayments.length}>All ({filteredPayments.length})</option>
            </select>
            <span style={{ fontSize: '14px', color: 'var(--color-text-light)' }}>
              Showing {startIndex + 1}-{Math.min(endIndex, filteredPayments.length)} of {filteredPayments.length} payments
            </span>
          </div>

          {/* Page numbers */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', justifyContent: 'center' }}>
              {/* Previous button */}
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                  backgroundColor: currentPage === 1 ? '#f5f5f5' : 'white',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                ← Previous
              </button>

              {/* Page numbers */}
              {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 7) {
                  pageNum = i + 1;
                } else if (currentPage <= 4) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 3) {
                  pageNum = totalPages - 6 + i;
                } else {
                  pageNum = currentPage - 3 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid var(--color-border)',
                      borderRadius: '4px',
                      backgroundColor: currentPage === pageNum ? 'var(--forest-green)' : 'white',
                      color: currentPage === pageNum ? 'white' : '#333',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: currentPage === pageNum ? 'bold' : 'normal'
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}

              {/* Next button */}
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                  backgroundColor: currentPage === totalPages ? '#f5f5f5' : 'white',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PaymentTracking;